import { useEffect, useRef } from 'react';

/**
 * Hook to continuously monitor YouTube player state changes
 * Helps debug mobile issues by tracking state transitions
 */
const usePlayerStateMonitor = (player, isReady, enabled = true) => {
  const lastStateRef = useRef({});
  
  useEffect(() => {
    if (!player || !isReady || !enabled) return;
    
    const interval = setInterval(() => {
      try {
        // Ensure player exists and has required methods
        if (!player || typeof player.getPlayerState !== 'function') {
          return;
        }
        
        const currentState = {
          playerState: player.getPlayerState(),
          isMuted: player.isMuted(),
          volume: player.getVolume(),
          currentTime: player.getCurrentTime(),
          duration: player.getDuration(),
          videoData: player.getVideoData()
        };
        
        // Compare with last state
        const lastState = lastStateRef.current;
        const changes = [];
        
        if (currentState.playerState !== lastState.playerState) {
          changes.push(`playerState: ${getStateName(lastState.playerState)} → ${getStateName(currentState.playerState)}`);
        }
        
        if (currentState.isMuted !== lastState.isMuted) {
          changes.push(`isMuted: ${lastState.isMuted} → ${currentState.isMuted}`);
        }
        
        if (currentState.volume !== lastState.volume) {
          changes.push(`volume: ${lastState.volume} → ${currentState.volume}`);
        }
        
        if (currentState.videoData?.video_id !== lastState.videoData?.video_id) {
          changes.push(`video: ${lastState.videoData?.video_id} → ${currentState.videoData?.video_id}`);
        }
        
        // Log changes if any
        if (changes.length > 0) {
          console.log('[PLAYER MONITOR] State changes:', {
            timestamp: new Date().toISOString(),
            changes: changes.join(', '),
            fullState: currentState
          });
        }
        
        // Update last state
        lastStateRef.current = currentState;
        
        // Store in window for debugging
        window.playerMonitorState = currentState;
      } catch (e) {
        console.error('[PLAYER MONITOR] Error:', e);
      }
    }, 500);
    
    return () => clearInterval(interval);
  }, [player, isReady, enabled]);
};

// Helper to get readable state name
const getStateName = (state) => {
  switch (state) {
    case -1: return 'UNSTARTED';
    case 0: return 'ENDED';
    case 1: return 'PLAYING';
    case 2: return 'PAUSED';
    case 3: return 'BUFFERING';
    case 5: return 'CUED';
    default: return `UNKNOWN(${state})`;
  }
};

export default usePlayerStateMonitor;